import React, { type FC, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Markup } from 'interweave'
import PinInput from 'react-pin-input'
import { Button, CircularProgress } from '@mui/material'
import { FormikProps, useFormik } from 'formik'
import * as Yup from 'yup'
import { AnyObject } from 'yup/es/types'
import Colors from '../../../utils/colors'
import AdditionalInfoForm from './AdditionalInfoForm'

interface OtpFormProps {
  setOtp: (otp: string) => void
  handleNext: (values: any) => void
  initInfo: any
  otp: string
  handleResend: (timer: any) => void
  otpError: string
  otpResponse: any
  isLoading: boolean
  additionalInfo?: any
  showOtp: boolean
  handleAdditionalInfo: (timer: any) => void
  isResendDisabled: boolean
  timer: any
  provider: number
}

export interface FormValues {
  [key: string]: any
}

interface IInfoLine {
  label: string
  value: string
}

const InfoLine: FC<IInfoLine> = ({ label, value }) => {
  return (
    <div className='flex flex-row items-center py-1 justify-between w-full'>
      <div className=' text-[14px]'>{label}</div>
      <div className=' text-[14px] text-right'>{value}</div>
    </div>
  )
}

export const OtpForm: React.FC<OtpFormProps> = ({
  setOtp,
  handleNext,
  initInfo,
  otp,
  otpResponse,
  handleResend,
  otpError,
  isLoading,
  additionalInfo,
  showOtp,
  handleAdditionalInfo,
  isResendDisabled,
  timer,
  provider,
}) => {
  const { t } = useTranslation()
  const info = initInfo.result
  const pin = React.useRef<PinInput | null>(null)
  const [isStateRequired, setIsStateRequired] = React.useState(false)

  const getRate = (benefit: number, transferInfo: number) =>
    ((benefit * 100) / (transferInfo * 100)).toFixed(2)

  function createYupSchema(schema: any, value: any = {}) {
    let validator = Yup.string()
    if (value.name === 'TrueReceiverDocIssueState') {
      validator = isStateRequired
        ? validator.required('Required')
        : (validator.nullable() as unknown as Yup.StringSchema<
            string | undefined,
            AnyObject,
            string | undefined
          >)
    } else validator = value.isRequired ? validator.required('Required') : validator
    // eslint-disable-next-line no-param-reassign
    schema[value.name] = validator
    return schema
  }

  const yepSchema = additionalInfo.reduce(createYupSchema, {})
  const validateSchema = Yup.object().shape(yepSchema)

  useEffect(() => {
    if (otpError) {
      setOtp('')
      pin.current?.clear()
    }
  }, [otpError])

  const initialValues = () => {
    if (additionalInfo) {
      const values: any = additionalInfo.reduce((acc: any, item: any) => {
        acc[item.name] = item.enumeratedValues ? null : ''
        return acc
      }, {})
      return { ...values }
    }
    return {}
  }

  const formik: FormikProps<FormValues> = useFormik({
    initialValues: initialValues() || {},
    validationSchema: validateSchema,
    onSubmit: () => {
      handleAdditionalInfo(timer)
    },
  })
  const borderColor = otpError && !otp ? Colors.primary.red : Colors.primary.main

  return (
    <div className='w-full h-full flex-1 flex flex-col items-center justify-center bg-white sm:p-[64px] p-4 '>
      {isLoading && (
        <div className='flex justify-center items-center absolute top-0 left-0 right-0 bottom-0 z-50 bg-white bg-opacity-50 backdrop-filter backdrop-blur-sm'>
          <CircularProgress color='success' />
        </div>
      )}
      {showOtp && additionalInfo ? (
        <AdditionalInfoForm
          formik={formik}
          t={t}
          additionalInfo={additionalInfo}
          setIsStateRequired={setIsStateRequired}
        />
      ) : (
        <div className='w-full h-full flex-1 container bg-infoBackground flex items-center justify-center'>
          <div className='h-full flex flex-col items-center justify-center sm:p-[64px] p-4 sm:w-10/12 w-full'>
            <div className='font-nexaBold text-[36px] text-center'>{t('t2c-page.otp.title')}</div>
            <div className='border-[1px] border-[#E5E5E5] w-full my-[16px] p-4 sm:w-3/4 md:w-2/4'>
              {info && info?.senderPartInfo.senderCustomerName && (
                <InfoLine
                  label={t('t2c-page.otp.sender')}
                  value={info.senderPartInfo.senderCustomerName}
                />
              )}
              {info && info?.senderPartInfo.senderCountry && (
                <InfoLine
                  label={t('t2c-page.otp.country')}
                  value={info.senderPartInfo.senderCountry}
                />
              )}
              {info && info?.moneyTransferInfo.amount && (
                <InfoLine
                  label={t('t2c-page.otp.bank')}
                  value={`${info.moneyTransferInfo.amount} ${info.moneyTransferInfo.currency.charCode}`}
                />
              )}
              {info && info?.moneyTransferInfo.currency.id !== 1 && (
                <InfoLine
                  label={t('t2c-page.otp.rate')}
                  value={`${getRate(
                    info?.customerBenefitInfo.amount,
                    info?.moneyTransferInfo.amount,
                  )} ${info?.customerBenefitInfo.currency.charCode}`}
                />
              )}
              <div className='h-[1px] bg-[#E5E5E5] w-full my-[16px]' />
              <div className='flex flex-row items-center py-1 justify-between w-full'>
                <div className=' font-nexaBold text-[16px]'>{t('t2c-page.otp.total-amount')}</div>
                <div className=' font-nexaBold text-[16px] text-right'>
                  {`${info?.customerBenefitInfo.amount} ${info?.customerBenefitInfo.currency.charCode}`}
                </div>
              </div>
            </div>
            <div className='sm:w-3/4 md:w-2/4 w-full flex items-center pb-4'>
              <Markup
                className='text-[14px] text-center w-full'
                content={t('t2c-page.otp.phone').replace('{s1}', info.phone)}
              />
            </div>
            {provider === 3 &&
              info.moneyTransferInfo.currency.charCode !==
                info?.customerBenefitInfo.currency.charCode && (
                <div className='sm:w-3/4 md:w-2/4 w-full flex items-center pb-4'>
                  {t('t2c-page.otp.mg-currency')}
                </div>
              )}
            <PinInput
              ref={pin}
              length={6}
              initialValue=''
              focus
              type='numeric'
              inputMode='number'
              inputStyle={{
                border: 'none',
                borderBottom: `2px solid ${borderColor}`,
                borderRadius: 0,
                fontSize: window.innerWidth > 620 ? '24px' : '14px',
                width: window.innerWidth > 620 ? '30px' : '20px',
                marginLeft: '5px',
                marginRight: '5px',
                textAlign: 'center',
              }}
              inputFocusStyle={{
                outline: 'none',
                borderBottom: `2px solid ${Colors.primary.secondary}`,
              }}
              onChange={setOtp}
              regexCriteria={/^[0-9]*$/}
            />
            {otpError && (
              <div className='text-red-500 text-[14px] text-center w-full pt-4'>
                {t(`${otpError}`)}
              </div>
            )}
            <div className='h-[30px]' />
            <Button
              id='t2cConfirmButton'
              variant='outlined'
              onClick={() => handleNext(formik.values ? formik.values : null)}
              disabled={otp.length !== 6}
              className='sm:w-3/4 md:w-2/4 w-full text-white border-2 mb-8 border-gray '
            >
              {t('p2p-page.otp.button')}
            </Button>
            {isResendDisabled ? null : (
              <button
                id='t2cResendButton'
                type='button'
                className='sm:mt-4 mt-2'
                onClick={() => {
                  handleResend(timer)
                }}
                disabled={timer.isRunning}
              >
                <div className='text-main duration-150 underline hover:no-underline ease-in-out text-center font-nexaBold text-[14px] mt-4'>
                  <Markup content={t('p2p-page.otp.resend')} />{' '}
                  {timer.isRunning &&
                    `(${timer.minutes}:${
                      timer.seconds < 10 ? `0${timer.seconds}` : timer.seconds
                    })`}
                </div>
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
export default OtpForm
